import { useRef } from 'react';
import { isEqual } from 'lodash';

import { Row, SortingState, TableOptions } from '@tanstack/react-table';

import type usePagination from 'hooks/usePagination';

import Table from './Table';

type ControlledTableProps<T> = Partial<TableOptions<T>> &
  Required<Pick<TableOptions<T>, 'columns' | 'data'>> & {
    loading: boolean;
    filters?: unknown;
    pageCount: number;
    pageSize?: number;
    pagination: ReturnType<typeof usePagination>;
    sorting: { sortBy: SortingState; setSortBy: (sort: SortingState) => void };
    onRowClick?: (row: Row<T>) => void;
    showColumnToggle?: boolean;
    userColumnVisibility?: Record<string, boolean>;
    onColumnToggle?: (id: string) => void;
  };

function ControlledTable<T>(props: ControlledTableProps<T>) {
  const {
    pageCount,
    pagination,
    sorting,
    state,
    onRowClick,
    showColumnToggle,
    userColumnVisibility,
    onColumnToggle,
    ...tableProps
  } = props;

  // Sorting sends the reader back to the first page — page 3 of the old order means nothing in
  // the new one. Table fires onSortChange on mount as well as on a real sort, though, so this
  // compares against the order already in force rather than resetting on every call: a table
  // whose page is restored from elsewhere (InboundReferralsListPage, whose page lives in its
  // store so it survives opening a referral) would otherwise be knocked back to page 1 the moment
  // it rendered. For a table holding its page in local state the guard changes nothing — it
  // starts on page 1 regardless.
  const sortInForce = useRef(sorting.sortBy);
  const handleSortChange = (next: SortingState) => {
    if (isEqual(sortInForce.current, next)) return;
    sortInForce.current = next;
    pagination.setPageIndex(0);
  };

  return (
    <Table<T>
      initialState={{ pagination: { pageIndex: pagination.paging.page - 1 || 0 } }}
      state={{
        ...(state ? state : {}),
        pagination: { pageIndex: pagination.paging.page - 1 || 0, pageSize: pagination.paging.pageSize },
      }}
      onPageChange={pagination.setPageIndex}
      onSortChange={handleSortChange}
      manualPagination
      manualSorting
      sorting={sorting}
      pageCount={pageCount}
      onRowClick={onRowClick}
      showColumnToggle={showColumnToggle}
      userColumnVisibility={userColumnVisibility}
      onColumnToggle={onColumnToggle}
      {...tableProps}
    />
  );
}

export default ControlledTable;
